import moment from 'moment-timezone';
import { getSelectedTimeZone } from '@/utils/dateTime';
import _ from 'lodash';
import { emptySpaceValidations, validationFunction } from '@/utils/generic';
import { ROLES } from '../users/user.constant';
import { REGEX } from '../regex.constant';

export enum SortEnum {
  asc = 'asc',
  desc = 'desc',
}

export enum EFeatureFlag {
  ENABLED = 'enabled',
  DISABLED = 'disabled',
}

export const TABS = [
  {
    label: 'Day',
    value: 'day',
  },
  {
    label: 'Week',
    value: 'week',
  },
  {
    label: 'Month',
    value: 'month',
  },
  {
    label: 'Year',
    value: 'year',
  },
];

export const HTTP_RESPONSES = {
  SUCCESS: {
    code: 200,
    message: 'Success',
  },
  CREATED: {
    code: 201,
    message: 'Success',
  },
  ACCEPTED: {
    code: 202,
    message: 'Success',
  },
  BAD_REQUEST: {
    code: 400,
    message: 'Bad Request. Please correct the request and resubmit.',
  },
  AUTHENTICATION_FAILED: {
    code: 401,
    message: 'Access Denied. Please provide valid authorization details.',
  },
  AUTHORIZATION_FAILED: {
    code: 403,
    message: 'Access Denied. Please provide valid authorization details.',
  },
  NOT_FOUND: {
    code: 404,
    message:
      'Unable to find the valid resource. Please provide a valid criteria or id.',
  },
  REQUEST_TIMEDOUT: {
    code: 408,
    message: 'Request timedout. Please try after some time.',
  },
  UNPROCESSABLE_CONTENT: {
    code: 422,
    message:
      'Validation error. Please correct the error and resubmit the request.',
  },
  SERVICE_UNAVAILABLE: {
    code: 503,
    message: 'Service unavailable, Please try after some time.',
  },
  INTERNAL_SERVER_ERROR: {
    code: 500,
    message: 'Unexpected Error. Please reach out to application support.',
  },
};

// HTTP Response Message
export const HTTP_RESPONSE_MESSAGES = {
  GENERIC: {
    NO_DATA: 'No data available',
  },
};

// Constants for common validation messages
export const COMMON_VALIDATIONS = {
  REQUIRED_VALIDATION_MSG: 'This field is required',
  MIN_MAX_VALIDATION_MSG:
    'Field length should be between 3 and 50 characters without empty spaces',
  INPUT_LENGTH:
    'Input cannot be less than 3 characters or more than 50 characters',
  SCP_BARCODE_VALIDATION_MSG:
    'Barcode min length is 3 and only AlphaNumerics, hyphens, colons are allowed.',
};

export const INPUT_LENGTH_MIN_MAX_VALUES = {
  MIN_LENGTH_VALUE: 3,
  MAX_LENGTH_VALUE: 50,
  PROVIDER_NAME_INPUT_MAX_LENGTH_ALLOWED: 32,
};

// Common validation rules used in form validation schemas
export const COMMON_VALIDATION_RULES = {
  validate: (value: any) => validationFunction(value),
  required: {
    value: true,
    message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
  },
};

// Common min/max validation rules used in form validation schemas
export const COMMON_MIN_MAX_VALIDATION_RULES = {
  minLength: {
    value: INPUT_LENGTH_MIN_MAX_VALUES.MIN_LENGTH_VALUE,
    message: COMMON_VALIDATIONS.MIN_MAX_VALIDATION_MSG,
  },
  maxLength: {
    value: INPUT_LENGTH_MIN_MAX_VALUES.MAX_LENGTH_VALUE,
    message: COMMON_VALIDATIONS.MIN_MAX_VALIDATION_MSG,
  },
  validate: (value: any) => emptySpaceValidations(value),
};

export const checkBarcodeValidationRule = {
  // Custom validation function
  validate: (value: any) => {
    if (!value) return true;
    const barcodes = value.split(',').map((item: string) => item.trim());
    const invalidBarcodes: string[] = [];
    const alphaNumeric = barcodes.every((barcode: string) => {
      if (barcode.length < 3) {
        invalidBarcodes.push(barcode);
        return false;
      }
      // Check if the barcode consists numerics, alphabetics & hyphens only
      return /^[a-zA-Z0-9-:]+$/.test(barcode)
        ? true
        : (invalidBarcodes.push(barcode), false);
    });
    return (
      alphaNumeric ||
      `${COMMON_VALIDATIONS.SCP_BARCODE_VALIDATION_MSG} Invalid Barcodes: ${invalidBarcodes.join(', ')}`
    );
  },
  // Standard required rule
  required: {
    value: true,
    message: COMMON_VALIDATIONS.REQUIRED_VALIDATION_MSG,
  },
};

// Types of validation used in form validation schemas
export const VALIDATION_TYPES = {
  REQUIRED: 'required',
  MIN_LENGTH: 'minLength',
  MAX_LENGTH: 'maxLength',
};

export const EMAIL_VALIDATION_RULES = {
  pattern: {
    value: REGEX.EMAILREGEX,
    message: 'Invalid Email Format',
  },
};

export const PASSWORD_VALIDATION_RULES = {
  pattern: {
    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d\S]{8,}$/,
    message:
      'Password must contain at least 1 uppercase letter, 1 lowercase letter, 1 number, and be at least 8 characters long.',
  },
};

export const ALL_LOCATION = {
  locationId: '1',
  sourceLocationId: '1',
  operatorId: '1',
  name: 'All Locations',
  createdOn: '',
  updatedOn: '',
};
export const ALL_DEVICE = {
  deviceId: '1',
  sourceId: 'All Devices',
  name: 'All Device',
  operatorId: '1',
  marketId: '1',
};

export const ALL_OPERATOR = { id: '1', name: 'All Operators', sourceId: '1' };
export const ALL_PROVIDER = {
  id: '1',
  name: 'All Providers',
  partnerUid: '',
  profile: 'All Providers',
};
export const ALL_SUB_APPLICATION = {
  id: '1',
  name: 'All SubApplications',
};
export const ALL_MARKET = {
  id: '1',
  marketId: '1',
  operatorName: 'All Operators',
  locationName: 'All Locations',
  locationId: '1',
  sourceLocationId: '1',
  sourceMarketId: '',
  marketsDropdownDisplayValue: 'All Markets',
  operatorId: '1',
  name: 'All Markets',
  createdOn: '',
  updatedOn: '',
};

export const DROPDOWN_OPTIONS = {
  ALL_MARKET_DROPDOWN_OPTION: {
    name: 'All Markets',
    id: '1',
    sourceId: '1',
    marketsDropdownDisplayValue: 'All Markets',
  },
  ALL_DEVICE_DROPDOWN_OPTION: {
    id: '1',
    sourceId: 'All Devices',
    name: 'All Device',
  },
  ALL_LOCATION_DROPDOWN_OPTION: {
    id: '1',
    sourceId: '1',
    name: 'All Locations',
  },
  ALL_OPERATOR_DROPDOWN_OPTION: {
    id: '1',
    sourceId: '1',
    name: 'All Operators',
  },
};

export const CARDLOADER = [1, 2, 3, 4, 5];
export const LOADER_LIMIT = 4;

export const pagination = { limit: 50, page: 1 };

export const START_DATE = 'startDate';

export const ROW_PER_PAGE_OPTION = [10, 25, 50, 100];

export const ROW_PER_PAGE_OPTION_LARGE = [100, 200, 300];

export const APP_COLOR_SETTINGS = {
  CTLP_PRIMARY: '#FF7F30',
  CTLP_ERROR: '#E75065',
};
export const LOGO_URL = 'https://qa.yokeportal.com/media/logos/logo-4.png';

const rolePermissions = (rolesAllowed: string[]) => ({
  CREATE: rolesAllowed,
  READ: [...rolesAllowed, ROLES.SUPPORT, ROLES.HUB_ACCESS],
  UPDATE: rolesAllowed,
  DELETE: rolesAllowed,
});

export const authorizeRole = {
  PORTAL: rolePermissions([ROLES.ADMIN, ROLES.MANAGER]),
  USERS: rolePermissions([ROLES.ADMIN, ROLES.HUB_ACCESS]),
};
export const MAX_LENGTH_IN_TABLE = 36;
export const NON_HYPHENED_UUID_LENGTH = 32;
export const TRUNCATED_SUFFIX = ' ....';
export const MAX_LENGTH_TRUNCATE_VALUE = 15;

export const ORGANISM_PROPS = {
  CONFIRM_ACTION_MODAL: {
    INPUT_LABEL: 'Confirm action',
    CONFIRM_LABEL: 'Confirm',
    CANCEL_LABEL: 'Cancel',
    DANGER_SUBTITLE: 'This action cannot be undone.',
  },
  COPY_BUTTON: {
    COPIED: 'Copied',
    COPY: 'Copy',
  },
  PROGRESS_PILL: {
    DONE: 'Done',
    RUNNING: 'Running',
    FAILED: 'Failed',
    CONFIG_MISSING: 'Config Missing',
  },
  FEATURE_FLAG_CARD: {
    ENABLED: 'Enabled',
    DISABLED: 'Disabled',
  },
  DASHBOARD_CHART: {
    VARIANT_AREA: 'Area',
    VARIANT_BAR: 'Bar',
    VARIANT_LINE: 'Line',
  },
  EXPORT_MODAL: {
    NO_RECORDS_TITLE: 'Nothing to Export',
    NO_RECORDS_SUBTITLE: 'Adjust your filters and try again',
    NO_RECORDS_EMPTY_STATE: 'No records to export',
    NO_RECORDS_HINT: 'Adjust your filters and try again',
    FORMAT_LABEL: 'File Format',
    RANGE_LABEL: 'Record Range',
    COLUMNS_LABEL: 'Export Columns',
    AT_LEAST_ONE_COLUMN: 'At least one column must be selected',
    GENERATING: 'Generating export…',
    RESET: 'Reset',
    EXPORT: 'Export',
    ALL: 'All',
    CLEAR: 'Clear',
    CSV_DESC: 'Spreadsheet · Excel compatible',
    PDF_DESC: 'Formatted · Print ready',
    JSON_DESC: 'Raw data · Developer friendly',
  },
  DATE_PICKER_POPUP: {
    ENABLE_APPLY_HINT: 'Select a date range to enable Apply.',
    CLOSE_TITLE: 'Close',
    DATE_RANGE_TITLE: 'Date Range',
    CLEAR_RANGE_TITLE: 'Clear range',
    NO_RANGE_SELECTED: 'No range selected',
  },
  DEVICE_EXPANDED: {
    SECTION_UUIDS: 'Internal UUIDs',
    SECTION_DETAILS: 'Device Details',
    LABEL_DEVICE_UUID: 'Device UUID',
    LABEL_OPERATOR_UUID: 'Operator UUID',
    LABEL_MARKET_UUID: 'Market UUID',
    LABEL_NAME: 'Name',
    LABEL_MODEL: 'Model',
    LABEL_LOCATION: 'Location',
    LABEL_LAST_TRANSACTION: 'Last Transaction',
    LABEL_CATALOG_DTM: 'Catalog Date',
  },
  ALERT_STATUS_CARD: {
    LABEL_PROCESSED: 'Processed',
    LABEL_PATCH: 'Patch',
    LABEL_STATUS_CODE: 'Status Code',
  },
  ALERT_MORE_DETAILS: {
    NO_PARTNER_DATA: 'No partner data',
    NO_CLIENT_DATA: 'No client data',
  },
  ALERT_FLOW_SECTION: {
    TITLE: 'Activity Flow',
    LOADING_MSG: 'Fetching activity flow…',
  },
  VDI_STEPPER: {
    LABEL_STATUS: 'Status',
    LABEL_TIMING: 'Timing',
    LABEL_VIA: 'Via',
    LABEL_MESSAGE: 'Message',
    SECTION_TITLE: 'Request Workflow',
    PARTNER_FLOW: 'Partner Flow',
    CLIENT_FLOW: 'Client Flow',
    CLICK_STEP_HINT: 'Click step to inspect',
    LOADING_MSG: 'Fetching workflow status…',
    NO_CONFIG_MSG:
      'No provider configuration found. The workflow stepper is not available.',
    STEP_CREATED_SUBTITLE: 'Transaction Initiated',
    STEP_GATEWAY_BADGE: 'Gateway',
    // Verb forms per step: pending → active → done → failed
    STEP_CREATE: 'Create', STEP_CREATING: 'Creating', STEP_CREATED: 'Created', STEP_CREATE_FAILED: 'Creation Failed',
    STEP_ACCEPT: 'Accept', STEP_ACCEPTING: 'Accepting', STEP_ACCEPTED: 'Accepted', STEP_ACCEPT_FAILED: 'Acceptance Failed',
    STEP_QUEUE: 'Queue', STEP_QUEUING: 'Queuing', STEP_QUEUED: 'Queued', STEP_QUEUE_FAILED: 'Queuing Failed',
    STEP_PROCESS: 'Process', STEP_PROCESSING: 'Processing', STEP_PROCESSED: 'Processed', STEP_PROCESS_FAILED: 'Processing Failed',
    STEP_SYNC: 'Sync', STEP_SYNCING: 'Syncing', STEP_SYNCED: 'Synced', STEP_SYNC_FAILED: 'Sync Failed',
    STEP_REPORT: 'Report', STEP_REPORTING: 'Reporting', STEP_REPORTED: 'Reported', STEP_REPORT_FAILED: 'Reporting Failed',
    // Tooltip hints per step × state  (pattern: "Waiting to…" | "{verb} started" | "{past} successfully" | "{noun} failed")
    TOOLTIP_CREATE_PENDING: 'Waiting for transaction to be created',
    TOOLTIP_CREATE_ACTIVE: 'Transaction creation started',
    TOOLTIP_CREATE_DONE: 'Transaction created successfully',
    TOOLTIP_CREATE_FAILED: 'Transaction creation failed',
    TOOLTIP_ACCEPT_PENDING: 'Waiting for acceptance',
    TOOLTIP_ACCEPT_ACTIVE: 'Acceptance started',
    TOOLTIP_ACCEPT_DONE: 'Transaction accepted successfully',
    TOOLTIP_ACCEPT_FAILED: 'Acceptance failed',
    TOOLTIP_QUEUE_PENDING: 'Waiting to be queued in the gateway',
    TOOLTIP_QUEUE_ACTIVE: 'Queuing in the gateway started',
    TOOLTIP_QUEUE_DONE: 'Queued in the gateway successfully',
    TOOLTIP_QUEUE_FAILED: 'Gateway queuing failed',
    TOOLTIP_PROCESS_PENDING: 'Waiting to be processed',
    TOOLTIP_PROCESS_ACTIVE: 'Processing started',
    TOOLTIP_PROCESS_DONE: 'Processing completed successfully',
    TOOLTIP_PROCESS_FAILED: 'Processing failed',
    TOOLTIP_SYNC_PENDING: 'Waiting to sync to partner',
    TOOLTIP_SYNC_ACTIVE: 'Syncing to partner started',
    TOOLTIP_SYNC_DONE: 'Synced to partner successfully',
    TOOLTIP_SYNC_FAILED: 'Partner sync failed',
    TOOLTIP_REPORT_PENDING: 'Waiting to report to clients',
    TOOLTIP_REPORT_ACTIVE: 'Reporting to clients started',
    TOOLTIP_REPORT_DONE: 'Reported to clients successfully',
    TOOLTIP_REPORT_FAILED: 'Client reporting failed',
  },
  VDI_ERRORS: {
    SECTION_TITLE: 'Errors',
  },
  VDI_PAYLOAD: {
    SECTION_LABEL: 'Payload',
  },
  SALES_EXPANDED: {
    LOADING_MSG: 'Loading order detail…',
    NO_PRODUCTS: 'No products',
    NO_PAYMENTS: 'No payments',
    NO_ORDER_TAXES: 'No order taxes',
    NO_SALES_TAXES: 'No sales taxes',
    NO_FEES: 'No fees',
    NO_TAXES_AND_FEES: 'No taxes & fees',
    NO_TAXES: 'No Taxes',
    LABEL_NAME: 'Name',
    LABEL_CODE: 'Code',
    LABEL_PRODUCT_ID: 'ProductId',
    LABEL_COST: 'Cost',
    LABEL_QUANTITY: 'Quantity',
    LABEL_PRICE: 'Price',
    LABEL_TAX_ID: 'Tax ID',
    LABEL_TAX_COUNT: 'Tax Count',
    LABEL_TAX_VALUE: 'Tax Value',
    LABEL_INCLUDED_IN_PRICE: 'Included in Price',
    LABEL_TAXES: 'Taxes',
    LABEL_PRODUCT_PRICE: 'Product Price',
    LABEL_FEES: 'Fees',
    LABEL_FEES_TOTAL: 'Fees Total',
    LABEL_TAXES_TOTAL: 'Taxes Total',
    LABEL_GROSS_TOTAL: 'Gross Total',
    LABEL_TYPE: 'Type',
    LABEL_AMOUNT: 'Amount',
    LABEL_REFERENCE: 'Reference',
    LABEL_TAX_NAME: 'Tax Name',
    LABEL_TAX_RATE: 'Tax Rate',
    LABEL_TAX_TOTAL: 'Tax Total',
    LABEL_FEE_NAME: 'Fee Name',
    LABEL_FEE_ID: 'Fee ID',
    LABEL_FEE_COUNT: 'Fee Count',
    LABEL_FEE_VALUE: 'Fee Value',
    LABEL_FEE_TOTAL: 'Fee Total',
    LABEL_SUBTOTAL: 'Subtotal',
    LABEL_FINAL_PRICE: 'Final Price',
    LABEL_ORDER_FEE: 'Order Fee',
    LABEL_ORDER_TAX: 'Order Tax',
    LABEL_SENT: 'Sent',
    LABEL_NOT_SENT: 'Not Sent',
  },
  SMART_COOLER_EXPANDED: {
    LOADING_MSG: 'Loading product detail...',
    NO_BARCODES: 'No barcodes attached to this product.',
    NO_TRANSACTIONS: 'No transaction history for this product.',
  },
  COOLER_TRANSACTION_EXPANDED: {
    NO_STEPPER_CONFIG: 'No stepper configuration.',
    LOADING_MSG: 'Fetching transaction details...',
    NO_DETAIL: 'No transaction detail available.',
  },
  CRON_JOB_EXPANDED: {
    NO_REPORT_DATA: 'No report data available.',
    NO_METADATA: 'No metadata available.',
    LABEL_TOTAL: 'Total',
    LABEL_METHOD: 'Method',
    LABEL_CLIENT_IP: 'Client IP',
    LABEL_REQUEST_TIMESTAMP: 'Request Timestamp',
  },
  APP_LOG_EXPANDED: {
    NO_META: 'No metadata available for this log.',
    SECTION_META: 'Metadata',
  },
  SMART_COOLER_EXPANDED_PANEL: {
    NO_SYSTEM_INFO: 'No system info available.',
    SECTION_IDENTITY: 'Device Identity',
    SECTION_TIMELINE: 'Connection Timeline',
    SECTION_SYSTEM: 'System Info',
    LABEL_COOLER_ID: 'Cooler ID',
    LABEL_NATIVE_ID: 'Native ID',
    LABEL_DEVICE_ID: 'Device ID',
    LABEL_DEVICE_SN: 'Device SN',
    LABEL_READER_SN: 'Reader SN',
    LABEL_DEVICE_TYPE: 'Device Type',
    LABEL_STATUS: 'Status',
    LABEL_SYSTEM_STATE: 'System State',
    LABEL_PARTNER_UID: 'Partner UID',
    LABEL_MARKET_ID: 'Market ID',
    LABEL_LAST_CONNECTED: 'Last Connected',
    LABEL_LAST_DISCONNECTED: 'Last Disconnected',
    LABEL_LAST_HEARTBEAT: 'Last Heartbeat',
    LABEL_LAST_FAILED_HEARTBEAT: 'Last Failed Heartbeat',
    LABEL_APP_VERSION: 'App Version',
    LABEL_OS_VERSION: 'OS Version',
    LABEL_NETWORK_TYPE: 'Network Type',
    LABEL_TIMEZONE: 'Timezone',
    LABEL_STORAGE_USED: 'Storage Used',
    LABEL_STORAGE_FREE: 'free of',
    LABEL_MB: 'MB',
    LABEL_SYSTEM_TIME: 'System Time',
  },
  VDI_EXPANDED: {
    LOADING_MSG: 'Loading transaction detail…',
  },
  GROUP_DROPDOWN: {
    PROVIDER_LABEL: 'Providers',
    PROVIDER_PLACEHOLDER: 'Select Provider',
    OPERATOR_LABEL: 'Operators',
    OPERATOR_PLACEHOLDER: 'Select Operator',
    LOCATION_LABEL: 'Locations',
    LOCATION_PLACEHOLDER: 'Select Location',
    MARKET_LABEL: 'Markets',
    MARKET_PLACEHOLDER: 'Select Market',
    DEVICE_LABEL: 'Devices',
    DEVICE_PLACEHOLDER: 'Select Device',
  },
  TOAST: {
    DEFAULT_ERROR_STATUS: 'Error',
  },
  SHORTCUTS_PANEL: {
    QUICK_SELECT: 'Quick Select',
    MODE_LAST: 'Last',
    MODE_THIS: 'This',
    TODAY: 'Today',
    LAST_2_DAYS: 'Last 2 Days',
    WEEK: 'Week',
    MONTH: 'Month',
    YEAR: 'Year',
  },
  TIME_DROPDOWN: {
    FUTURE_LABEL: 'Future',
  },
  TIME_SELECTOR: {
    HEADER: 'Time Selection',
    FROM_LABEL: 'From',
    TO_LABEL: 'To',
    START_LABEL: 'Start',
    END_LABEL: 'End',
    NOW_LABEL: 'Now',
  },
  CALENDAR_VIEW: {
    DAYS_LABEL: 'Days',
    TOTAL_LABEL: 'Total',
    NEW_START: 'New Start',
    RANGE_END: 'Range End',
    WEEKDAYS: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  },
  ALERT_HEADER_META: {
    STATUS_LABEL: 'Status',
  },
  ALERT_TIMELINE_ITEM: {
    TOOLTIP_STATUS_PREFIX: 'Status: ',
    TOOLTIP_MESSAGE_PREFIX: 'Message: ',
    TOOLTIP_SEPARATOR: ' \u2014 ',
    TOOLTIP_NO_DETAILS: 'No additional details',
    META_STATUS_PREFIX: ' | Status: ',
  },
  ALERT_TIMING_CARD: {
    SECTION_TITLE: 'Alert Timing',
    SENT_ON: 'Sent On',
    VALIDATED_ON: 'Validated On',
    PROCESSED_ON: 'Processed On',
    CLOSED_ON: 'Closed On',
    CREATED_ON: 'Created On',
    UPDATED_ON: 'Updated On',
    N_A: 'N/A',
  },
};

export const ELEMENTS_PROPS = {
  CONFIG_ACCORDION: {
    FIX_REQUIRED: 'Fix required',
    CONFIG_ENABLED: 'Configuration enabled',
    CONFIG_DISABLED: 'Configuration disabled',
  },
  CONFIG_DROPDOWN: {
    DEFAULT_PLACEHOLDER: 'Select...',
  },
  DATE_PICKER: {
    INVALID_DATE: 'Invalid Date',
  },
  DISCLOSURE_BUTTON: {
    OPEN_LABEL: 'More Details',
    CLOSE_LABEL: 'Hide',
  },
  EXPANDABLE_LIST: {
    STATUS_ACTIVE: 'Active',
    STATUS_DEACTIVATED: 'Deactivated',
  },
  PAGE_TOOLBAR: {
    FILTERS_LABEL: 'Filters',
  },
  STEPPER: {
    EMPTY_MESSAGE: 'No configuration found for this provider.',
    ACTION_STATUS_BREAKDOWN: 'Action status breakdown',
  },
};

export const AUTOCOMPLETE_PROPS = {
  EMPTY: {
    NO_DATA: 'No Data available.',
    NO_RESULTS_TITLE: 'No results',
    NO_RESULTS_SUBTITLE: 'No match for',
    CLEAR_SEARCH_TITLE: 'Clear search',
    CLEAR_SEARCH_LABEL: 'Clear search',
  },
  PRIMARY_OPTION: {
    USE_ALL: 'Use all available options',
    CLEAR_SELECTION: 'Clear the current selection',
  },
  CUSTOM_SUFFIX: ' (Custom)',
};

export const SANDSTAR_PROVIDER = {
  NAME: 'SANDSTAR',
  PARTNERID: 'PARTNER-PEPSICO-SS',
};
export const SMART_CAFE_PARTNERID = 'PARTNER-SC-CTLP';

export const getTimeWithZone = (originalTime: string) => {
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timeZoneLabel = moment.tz(userTimeZone).format('z');

  // Convert IST time to the user's local time zone
  const timeInLocal = moment.tz(originalTime, userTimeZone).format('lll');
  return `${timeInLocal} ${timeZoneLabel}`;
};

export const EActiveStatus = [
  {
    id: 'all',
    name: 'All',
    includeInactive: true,
  },
  {
    id: '1',
    name: 'Active',
  },
  {
    id: '0',
    name: 'In Active',
    inactive: true,
  },
];

export const SELECTED_TIMEZONE_LOCALSTORAGE_KEY = 'selectedTimeZone';

export const DOWNLOAD_ERROR_MESSAGE: Record<number | string, string> = {
  400: 'Bad request while downloading file',
  401: 'Unauthorized access',
  403: 'Access forbidden',
  404: 'File not found',
  500: 'Internal server error',
  DEFAULT: 'Unable to download file',
  UNEXPECTED: 'Unexpected error occurred while downloading file',
  FILE_DOWNLOAD_FAILED: 'File download failed:',
};

export const STRINGS = {
  VDI_TXN: 'VDI Transactions',
  VDI_TXN_SUB: 'See details about all vdi requests',
  QUICK_PICK: 'QuikPick Master Products',
  QUICK_PICK_SUB: 'See information about Master Product Catalog',
  SMART_COOLER_PRODUCTS: 'Smart Cooler Products',
  SMART_COOLER_PRODUCTS_SUB: 'See information about Smart Cooler Products',
  COOLER_TRANSACTIONS: 'Smart Cooler Transactions',
  COOLER_TRANSACTIONS_SUB: 'View and manage smart cooler transaction data',
  MARKET: 'Markets',
  MARKET_SUB: 'See information about all market list',
  DEVICE: 'Devices',
  DEVICE_SUB: 'See information about all Devices',
  PRODUCTS: 'Products',
  PRODUCTS_SUB: 'See information about all product list',
  SALES: 'Sales',
  SALES_SUB: 'See information about all Sales report',
  PROVIDER: 'Providers',
  PROVIDER_SUB: 'These are details about the providers',
  OPERATOR: 'Operators',
  OPERATOR_SUB: 'These are details about the operators',
  RECONCI: 'Reconciliation',
  RECONCI_SUB: 'See information about all reconciliation report',
  USER: 'Users',
  USER_SUB: 'These are details about the users',
  ALERTS: 'Smart Cooler Alerts',
  ALERTS_SUB: 'See information about all Alerts report',
  CRON_JOBS_TRACKER: 'CronJobs',
  CRON_JOBS_TRACKER_SUB: 'See Information about all Cronjobs Data',
  TARGETED_RECONCILIATION: 'Targeted Reconciliation',
  TARGETED_RECONCILIATION_SUB:
    'See information about all targeted reconciliation records',
  STANDALONE_HEADER: 'Trained Product List',
  STANDALONE_SUB: 'Use for Planogram Setup',
};
export const ISO_FORMAT = 'YYYY-MM-DD';
export const SELECTED_TIMEZONE = getSelectedTimeZone();

export const COMMON_DROPDOWN_CONSTANTS = {
  SELECTORS: {
    PROVIDERS: 'PROVIDERS',
    OPERATORS: 'OPERATORS',
    LOCATIONS: 'LOCATIONS',
    MARKETS: 'MARKETS',
    DEVICES: 'DEVICES',
  },
  FETCH_MORE_TYPE: {
    OPERATORS_KEY: 'id',
    LOCATIONS_ID_KEY: 'locationId',
    MARKETS_ID_KEY: 'marketId',
    DEVICES_ID_KEY: 'deviceId',
  },
};

export const LOCAL_STORAGE_KEYS = {
  SELECTED_TIMEZONE: 'selectedTimeZone',
  PROVIDER_IN_LOCAL_STORAGE: 'providerVal',
  OPERATOR_IN_LOCAL_STORAGE: 'operatorVal',
};

export const defaultSortSymbolStyling = `ml-2 font-extrabold text-slate-900`;
export const defaultSortSymbolStyling2 = `ml-2 font-extrabold text-white`;

export const STATUS_CODES = {
  SUCCESS: 200,
  CREATED: 201,
  ACCEPTED: 202,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  CONFLICT: 409,
  UNPROCESSABLE_ENTITY: 422,
  INTERNAL_SERVER_ERROR: 500,
  ZERO: 0,
};
export const REPORT_FILE_NAMES = {
  VDI_TRANSACTION_REPORT: 'vdi-transaction-report',
  QUICK_PICK_MASTER_PRODUCT_REPORT: 'quick-pick-master-product-report',
  SMART_COOLER_PRODUCTS_REPORT: 'smart-cooler-products-report',
  DEVICES_REPORT: 'devices-report',
  MARKETS_REPORT: 'markets-report',
  PRODUCTS_REPORT: 'products-report',
  MMS_SALES_REPORT: 'mms-sales-report',
};

export const MODAL_TITLES = {
  DEVICE_PAGE: 'Devices',
  SMART_COOLER_PAGE: 'Smart Cooler Products',
  MARKETS_PAGE: 'Markets',
  MMS_SALES_PAGE: 'MMS Sales',
  QUICK_PAY_PAGE: 'Quick Pay Products',
  PRODUCTS_PAGE: 'Products',
};

export const COMMON_TABLE_STYLES = {
  TABLE_BODY: 'relative max-h-[85vh] scrollbar-hide overflow-x-auto',
};

// Alerts and CoolerTransactions Page Stepper content
export const COMMON_STEPPER_CONTENT = {
  STEPPER_TITLES: {
    MMS: 'MMS',
    GATEWAY: 'GATEWAY',
    VMS: 'VMS',
    CLIENTS: 'CLIENTS',
  },
  ACTIONS: {
    GATEWAY_STEP_ACTIONS: {
      CREATE: 'CREATE',
      UPDATE: 'UPDATE',
    },
    VMS_STEP_ACTIONS: {
      CREATE_SYNC: 'CREATE-SYNC',
      UPDATE_SYNC: 'UPDATE-SYNC',
      GET_SYNC: 'GET-SYNC',
    },
    CLIENT_STEP_ACTIONS: {
      CREATE_SYNC: 'CREATE-SYNC',
      GET_SYNC: 'GET-SYNC',
      UPDATE_SYNC: 'UPDATE-SYNC',
    },
    MMS_STEP_ACTIONS: {
      CREATE_SYNC: 'CREATE-SYNC',
      UPDATE_SYNC: 'UPDATE-SYNC',
    },
    STATUS: {
      SUCCESS: 'Success',
      FAILED: 'Failed',
      NOT_STARTED: 'Not Started',
    },
  },
} as const;

export const STANDALONE_BASE_PATH = '/index.html';

export const ERROR_PAGES = {
  FORBIDDEN: {
    CODE: '403',
    TITLE: 'Access Denied',
    BODY: "You don't have permission to access this resource. Please contact your system administrator if you believe this is a mistake.",
    SIGN_OUT: 'Sign Out',
  },
  NOT_FOUND: {
    CODE: '404',
    TITLE: 'Page not found',
    BODY: "Looks like you've ventured into the void. The page you're looking for doesn't exist or has been moved.",
    CTA: 'Back to Dashboard',
  },
};
